import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertCircle, CheckCircle2, Info } from "lucide-react";

type ToastKind = "info" | "success" | "error";
type ToastInternal = { id: number; kind: ToastKind; message: string };

type ToastApi = {
  show: (kind: ToastKind, message: string) => void;
  info: (m: string) => void;
  success: (m: string) => void;
  error: (m: string) => void;
};

const Ctx = createContext<ToastApi | null>(null);

export function useToast() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useToast must be used inside <ToastProvider>");
  return v;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastInternal[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts((cur) => cur.filter((t) => t.id !== id));
  }, []);

  const show = useCallback((kind: ToastKind, message: string) => {
    const id = Date.now() + Math.random();
    setToasts((cur) => [...cur, { id, kind, message }]);
    window.setTimeout(() => dismiss(id), 3500);
  }, [dismiss]);

  const api: ToastApi = {
    show,
    info: (m) => show("info", m),
    success: (m) => show("success", m),
    error: (m) => show("error", m),
  };

  return (
    <Ctx.Provider value={api}>
      {children}
      <ToastViewport toasts={toasts} />
    </Ctx.Provider>
  );
}

function ToastViewport({ toasts }: { toasts: ToastInternal[] }) {
  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 z-50 flex flex-col items-center gap-2 pointer-events-none"
      style={{ bottom: `calc(env(safe-area-inset-bottom) + 84px)` }}
    >
      <AnimatePresence>
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} />
        ))}
      </AnimatePresence>
    </div>
  );
}

function ToastItem({ toast }: { toast: ToastInternal }) {
  const [, force] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => force((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, []);

  const accent =
    toast.kind === "error"
      ? "var(--system-red)"
      : toast.kind === "success"
        ? "var(--system-green)"
        : "var(--system-blue)";

  const Icon =
    toast.kind === "error" ? AlertCircle : toast.kind === "success" ? CheckCircle2 : Info;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 8, scale: 0.96 }}
      transition={{ type: "spring", stiffness: 320, damping: 26 }}
      className="material flex items-center gap-2 px-4 py-3 text-callout pointer-events-auto"
      style={{
        borderRadius: 14,
        boxShadow: "var(--shadow-elevated)",
        color: "var(--label-primary)",
        maxWidth: 420,
      }}
    >
      <Icon size={18} style={{ color: accent }} />
      <span>{toast.message}</span>
    </motion.div>
  );
}
